import Link from 'next/link';
import { NextSeo, FAQPageJsonLd } from 'next-seo';
import Layout from '@components/common/Layout';
import cn from 'classnames';
import { CheckIcon } from '@heroicons/react/24/outline';
import { getServicesPage } from '@lib/api';
export default function Services({
  title,
  metaTitle,
  metaDescription,
  servicesOffered,
  faqs,
  cta,
}) {
  return (
    <>
      <NextSeo
        title={metaTitle}
        titleTemplate="%s"
        description={metaDescription}
        canonical="https://www.hlbelectric.com/electrical-services"
        openGraph={{
          title: `${metaTitle} | HLB Electric`,
          url: 'https://www.hlbelectric.com/electrical-services',
          description: `${metaDescription}`,
        }}
      />
      <FAQPageJsonLd mainEntity={faqs} />
      <div className="bg-gradient-to-b from-red-50 via-white to-white">
        {/* Pricing section with single price and feature list */}
        <div className="mx-auto max-w-7xl py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="pb-16 xl:flex xl:items-center xl:justify-between">
            <div>
              <h1 className="text-4xl font-extrabold sm:text-5xl sm:tracking-tight">
                <span className="text-gray-900">{title}</span>
              </h1>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-16 xl:grid xl:grid-cols-3 xl:gap-x-8">
            <div>
              <h2 className="text-base font-semibold uppercase tracking-wide text-red-600">
                Everything you need
              </h2>
              <p className="mt-2 text-3xl font-extrabold text-gray-900">
                Your preferred electrician in Maricopa
              </p>
              <p className="mt-4 text-lg text-gray-500">
                We go the extra mile on every project. The value we provide
                clients comes from our level of skill and performance, as well
                as our knowledge and professionalism. Rest assured, we put the
                same level of energy into every project we take on.
              </p>
            </div>
            <div className="mt-4 sm:mt-8 md:mt-10 md:grid md:grid-cols-2 md:gap-x-8 xl:col-span-2 xl:mt-0">
              <ul role="list" className="divide-y divide-gray-200">
                {servicesOffered.slice(0, 7).map((service, serviceIdx) => (
                  <li
                    key={service}
                    className={cn(
                      serviceIdx === 0 ? 'md:py-0 md:pb-4' : '',
                      'flex py-4'
                    )}
                  >
                    <CheckIcon
                      className="h-6 w-6 shrink-0 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="ml-3 text-base text-gray-500">
                      {service}
                    </span>
                  </li>
                ))}
              </ul>
              <ul
                role="list"
                className="divide-y divide-gray-200 border-t border-gray-200 md:border-t-0"
              >
                {servicesOffered.slice(7).map((service, serviceIdx) => (
                  <li
                    key={service}
                    className={cn(
                      serviceIdx === 0 ? 'md:border-t-0 md:py-0 md:pb-4' : '',
                      'flex py-4'
                    )}
                  >
                    <CheckIcon
                      className="h-6 w-6 shrink-0 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="ml-3 text-base text-gray-500">
                      {service}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Branded FAQ */}
      <div className="bg-gray-900">
        <div className="mx-auto max-w-7xl py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white">
            Frequently asked questions
          </h2>
          <div className="mt-6 border-t border-gray-400 border-opacity-25 pt-10">
            <dl className="space-y-10 md:grid md:grid-cols-2 md:grid-rows-2 md:gap-x-8 md:gap-y-12 md:space-y-0">
              {faqs.map((item) => (
                <div key={item.questionName}>
                  <dt className="text-lg font-bold leading-6 text-white">
                    {item.questionName}
                  </dt>
                  <dd className="mt-2 text-base text-gray-100">
                    {item.acceptedAnswerText}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-red-50">
        <div className="mx-auto max-w-7xl py-12 px-4 sm:px-6 lg:flex lg:items-center lg:justify-between lg:py-24 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl">
            <span className="block">{cta.title}</span>
            <span className="block bg-gradient-to-r from-rose-600 to-red-600 bg-clip-text text-transparent">
              {cta.subtitle}
            </span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                href={cta.route}
                prefetch={false}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-rose-600 to-red-600 py-3 px-5 text-base font-medium text-white hover:from-rose-700 hover:to-red-700"
              >
                {cta.buttonText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const data = await getServicesPage();

  const { title, metaTitle, metaDescription, servicesOffered, faqs, cta } =
    data[0];

  return {
    props: {
      title,
      metaTitle,
      metaDescription,
      servicesOffered,
      faqs,
      cta,
    },
  };
}

Services.Layout = Layout;
